import { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@lmig/lmds-react-table';
import './PolicyLookup.scss';
import { useUserData } from 'Foundation/User/UserContext';
import { Heading } from '@lmig/lmds-react-typography';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import AccessDeniedMessage from './Helpers/AccessDeniedMessage';
import { getBLAxisBaseURL, getBLPolicyDocumentLink } from 'Foundation/Configurations/GlobalConfiguration';

function PolicySummary(props: { policyNumber: string; callback: () => void }) {
  const [policies, setPolicies] = useState<{ data?: IPolicyLookupResults[] | null; isLoaded: boolean }>({
    isLoaded: false,
  });
  const { userData } = useUserData();
  const [responseStatus, setResponseStatus] = useState<{ statusCode: number; message: string }>({
    statusCode: HttpStatusCodes.InternalServerError,
    message: '',
  });
  const defaultMessage = 'No policy activity found for this customer for the last 13 months.';

  useEffect(() => {
    const fetchData = async () => {
      setPolicies({ isLoaded: false });
      let response = (await SitecoreFetch(
        `/api/sitecore/PolicyAsync/GetPolicyLookupResults?agencyNumber=${userData?.selectedStatCodeData?.SubStat}&policyNumber=${props.policyNumber}`,
      )) as IServerResponse;
      setResponseStatus({ statusCode: response.Status.Code, message: response.Status.Message });
      let result: IPolicyLookupResults[] | null;
      if (response.Status.Code === HttpStatusCodes.OK) result = response.Data as IPolicyLookupResults[];
      else result = null;
      setPolicies({ data: result, isLoaded: true });
      props.callback();
    };
    
    try {
      fetchData();
    } catch (error) {
      console.error(error);
    }
  }, [props.policyNumber, userData?.selectedStatCodeData?.SubStat]);

  return (
    <>
      {userData && (
        <div>
          <Heading type="h4-light">Policy Summary</Heading>
          {!policies.isLoaded ? (
            <CircularProgressSpinner size="32" aria-label="Loading recent activities data..." />
          ) : policies.data && policies.data.length > 0 ? (
            <Table id="policy-lookup-policy-table" className="uscm-recent-activities-table" rowStyle="zebra">
              <TableHeader>
                <TableRow>
                  <TableCell type="colHead">Policy</TableCell>
                  <TableCell type="colHead">LOB</TableCell>
                  <TableCell type="colHead">Process Date</TableCell>
                  <TableCell type="colHead">Effective Date</TableCell>
                  <TableCell type="colHead">Doc Type</TableCell>
                  <TableCell type="colHead">Transaction Type</TableCell>
                  <TableCell type="colHead">Pages</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policies.data.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      {!userData.isAISGUser || userData.isMockingAgent ? (
                        <form
                          name="apodNavLinksForm"
                          method="post"
                          action={getBLPolicyDocumentLink(userData?.isAgent())}
                          target="policy-document"
                          onSubmit={() => window.open('about:blank', 'policy-document', 'width=1000,height=800')}
                        >
                          <input type="hidden" name="documentNumber" value={item.DocumentNumber}></input>
                          <input type="hidden" name="policyNumber" value={item.PolicyNumber}></input>
                          <input
                            type="hidden"
                            name="agencyNumber"
                            value={userData?.selectedStatCodeData?.SubStat}
                          ></input>
                          <input type="hidden" name="lob" value={item.LOB}></input>
                          <input type="hidden" name="" value=""></input>
                          <input type="hidden" name="market" value="C"></input>
                          <input type="hidden" name="fromCustView" value="YES"></input>
                          <input type="hidden" name="fromRecentAct" value="YES"></input>
                          <input
                            type="submit"
                            className="submitLinkStyle lm-Link lm-LinkInline"
                            value={item.PolicyNumber}
                          ></input>
                        </form>
                      ) : (
                        <button
                          type="button"
                          className="submitLinkStyle lm-Link lm-LinkInline"
                          onClick={() => {
                            const axisBaseUrl = getBLAxisBaseURL();
                            const accountNumberForAxis =
                              item.PolicyNumber.length > 8 ? item.PolicyNumber.slice(-8) : item.PolicyNumber;
                            const redirectUrl = `${axisBaseUrl}/business/${accountNumberForAxis}/policy/${item.PolicyNumber}`;
                            window.open(redirectUrl, '_blank');
                          }}
                        >
                          {item.PolicyNumber}
                        </button>
                      )}
                    </TableCell>
                    <TableCell>{item.LOB}</TableCell>
                    <TableCell>{item.ProcessDate}</TableCell>
                    <TableCell>{item.EffectiveDate}</TableCell>
                    <TableCell>{item.DocType}</TableCell>
                    <TableCell>{item.TransactionType}</TableCell>
                    <TableCell>{item.NumberOfPages}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : responseStatus.statusCode === HttpStatusCodes.OK ? (
            defaultMessage
          ) : (
            <AccessDeniedMessage
              status={responseStatus.statusCode}
              responseMessage={responseStatus.message}
              activityDataType={'policy'}
            ></AccessDeniedMessage>
          )}
        </div>
      )}
    </>
  );
}

export default PolicySummary;
